import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useFetch from '../../../../components/useFetch';
import '../../Admin.scss';
import {
    AnalyticsIcon,
    EmailIcon,
    HistoryIcon,
    TenantIcon,
    UserIcon,
    MenuIcon,
    DatasetIcon,
    ModelEvaluationIcon,
    ModelTrainingIcon,
    TableIcon,
    LabelsIcon,
    GearIcon,
    LinkIcon
} from '../../../Common/SvgIcons/WorkflowIcons';
import { RecentLogs } from '../Common/RecentLogs';
import { Skeleton } from '@mui/material';
import TitleHeader from '../../../project/header/TitleHeader';
import PathBreadcrumb from '../../../../components/PathBreadcrumb/PathBreadcrumb';

export const UserDetails = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const role = localStorage.getItem("role");
    const viewFrom = location.state?.viewFrom;
    const userId = location.state?.id || localStorage.getItem('user_id');
    const isOwnProfile = String(userId) === String(localStorage.getItem('user_id'));
    const showAdminBackButton =
        (role === 'system_admin' || role === 'tenant_admin') &&
        viewFrom !== 'profile_dashboard' &&
        !isOwnProfile;

    const { data, loading, error, refetch } =
        useFetch(userId ? `users/${userId}` : null, "get");

    /* ---------------- GROUP RESOURCES ---------------- */

    const groupedResources = useMemo(() => {
        if (!data?.resources) return {};

        return Object.entries(data.resources).reduce((acc, [key, resource]) => {
            const category = resource.category || "core";

            if (!acc[category]) acc[category] = [];

            acc[category].push({
                key,
                ...resource
            });

            return acc;
        }, {});
    }, [data]);

    const profileMetrics = useMemo(() => {
        if (!data) return null;

        const resources = Object.values(data.resources || {});
        const limited = resources.filter((resource) => {
            const limit = Number(resource.limit || 0);
            return limit > 0 && limit < 9999;
        });
        const avgUsage = limited.length
            ? Math.round(
                limited.reduce((sum, resource) => {
                    const usage = Number(resource.usage || 0);
                    const limit = Number(resource.limit || 1);
                    return sum + Math.min((usage / limit) * 100, 100);
                }, 0) / limited.length
            )
            : 0;

        const rawRole = Array.isArray(data.roles)
            ? data.roles[0]
            : data.roles || data.role || "—";

        return {
            status: data.status || "—",
            role: String(rawRole).replace(/_/g, " "),
            resourceCap: `${avgUsage}%`,
            quotas: resources.length,
            memberSince: data.created_at
                ? new Date(data.created_at).toLocaleDateString()
                : "—",
        };
    }, [data]);

    const pageTitle1 = isOwnProfile ? "My" : "User";
    const pageTitle2 = isOwnProfile ? " Account" : " Management";
    const pageSubtext = isOwnProfile
        ? "Your account overview, quotas, and security settings."
        : "Review member access, quotas, and account activity.";

    /* ---------------- CATEGORY ICONS ---------------- */

    const categoryIcons = {
        core: <MenuIcon className="wf-icon-yellow p-1 border-0" style={{ width: 22, height: 22 }} />,
        data: <DatasetIcon className="wf-icon-blue p-1 border-0" style={{ width: 22, height: 22 }} />,
        training: <ModelTrainingIcon className="wf-icon-green p-1 border-0" style={{ width: 22, height: 22 }} />,
        evaluation: <ModelEvaluationIcon className="wf-icon-green p-1 border-0" style={{ width: 22, height: 22 }} />,
        deployment: <HistoryIcon className="wf-icon-blue p-1 border-0" style={{ width: 22, height: 22 }} />,
        solutions: <TableIcon className="wf-icon-purple p-1 border-0" style={{ width: 22, height: 22 }} />,
        labeling: <LabelsIcon className="wf-icon-blue p-1 border-0" style={{ width: 22, height: 22 }} />,
    };

    const getCategoryIcon = (category) => {
        return categoryIcons[category?.toLowerCase()] ||
            <MenuIcon className="wf-icon-gray p-1" style={{ width: 22, height: 22 }} />;
    };

    const getColor = (percentage) => {
        if (percentage > 85) return "#dc3545";
        if (percentage > 60) return "#ffc107";
        return "var(--leading-color)";
    };

    const goToSettings = () => {
        navigate('/admin/user-profile-edit', {
            state: {
                user: data,
                id: userId || data?.id,
                viewFrom: isOwnProfile ? 'profile_dashboard' : 'user_details',
            },
        });
    };

    if (loading) {
        return (
            <>

                <div className="mainmenu projects-content de-width">
                    {showAdminBackButton && (
                        <PathBreadcrumb
                            onBack={() => navigate(-1)}
                            items={[
                                { label: 'Admin', onClick: () => navigate('/admin') },
                                { label: 'Users', onClick: () => navigate(-1) },
                                { label: 'User' },
                            ]}
                        />
                    )}

                    <div className={`d-a justify-content-between ${isOwnProfile ? 'de-header-tag' : ''} gap-3`}>
                        <div className="d-a gap-3 flex-fill">
                            <Skeleton
                                variant="rounded"
                                width={40}
                                height={40}
                                className="landing-page-main-icon flex-shrink-0"
                            />
                            <div className="title-header__copy">
                                <Skeleton variant="text" width={220} height={36} />
                                <Skeleton variant="text" width={280} height={22} className="mt-1" />
                            </div>
                        </div>
                    </div>

                    {isOwnProfile && (
                        <div className='admin-metric-row mt-4 pb-2'>
                            {[...Array(4)].map((_, idx) => (
                                <div
                                    key={idx}
                                    className="container-card dashboard-cards position-relative overflow-hidden"
                                    style={idx === 0 ? { borderLeft: '4px solid var(--leading-color)' } : {}}
                                >
                                    <Skeleton variant="circular" width={28} height={28} />
                                    <Skeleton variant="text" width={70} height={18} />
                                    <Skeleton variant="text" width={36} height={22} />
                                </div>
                            ))}
                        </div>
                    )}

                    <div className='d-flex gap-4 admin-content mt-4'>
                        <div className='admin-content-card1'>
                            <section className='container-card d-flex align-items-start justify-content-between'>
                                <Skeleton variant="circular" width={56} height={56} />

                                <div className='flex-fill ms-2'>
                                    <div className='d-a gap-3 flex-wrap'>
                                        <Skeleton variant="text" width={180} height={34} />
                                        <Skeleton variant="rounded" width={70} height={22} />
                                    </div>

                                    <div className='d-flex flex-wrap align-items-start align-items-sm-center flex-column flex-sm-row gap-2 mt-1 mb-2'>
                                        <Skeleton variant="text" width={90} height={20} />
                                        <Skeleton variant="text" width={180} height={20} />
                                    </div>

                                    <div className='d-a gap-2'>
                                        <Skeleton variant="text" width={120} height={18} />
                                    </div>
                                </div>
                            </section>

                            <div className='d-a gap-2 mb-3 mt-4'>
                                <Skeleton variant="circular" width={22} height={22} />
                                <Skeleton variant="text" width={170} height={24} />
                            </div>

                            <section className='mt-2 gap-4'>
                                {[...Array(3)].map((_, sectionIdx) => (
                                    <section
                                        key={sectionIdx}
                                        className='container-card admin-quota-category mb-4 p-4'
                                    >
                                        <Skeleton variant="text" width={140} height={28} className="mb-4" />

                                        <div className='admin-quota-grid'>
                                            {[...Array(2)].map((_, idx) => (
                                                <div
                                                    key={idx}
                                                    className='admin-quota-item'
                                                >
                                                    <div className='d-flex justify-content-between mb-1 flex-wrap gap-2 admin-quota-item__header'>
                                                        <Skeleton variant="text" width={140} height={24} />
                                                        <Skeleton variant="text" width={100} height={24} />
                                                    </div>

                                                    <Skeleton variant="rounded" width="100%" height={8} className="admin-quota-item__bar" />
                                                    <Skeleton variant="text" width={100} height={18} className="mt-2 admin-quota-item__consumed" />
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                ))}
                            </section>
                        </div>

                        <div className='admin-content-card2'>
                            <section className="container-card">
                                <Skeleton variant="text" width={120} height={24} className="mb-3" />
                                {[...Array(4)].map((_, idx) => (
                                    <div key={idx} className="mb-3">
                                        <Skeleton variant="rounded" width="100%" height={44} />
                                    </div>
                                ))}
                            </section>
                        </div>
                    </div>
                </div>

                <div className="mobile-view-bottom"></div>
            </>
        );
    }

    return (
        <>

            <div className={`mainmenu de-width`}>

                {showAdminBackButton &&
                    <PathBreadcrumb
                        onBack={() => navigate(-1)}
                        items={[
                            { label: 'Admin', onClick: () => navigate('/admin') },
                            { label: 'Users', onClick: () => navigate(-1) },
                            { label: 'User' },
                        ]}
                    />}


                <div className={` d-a justify-content-between ${isOwnProfile ? 'de-header-tag' : ''} gap-3`}>
                    <TitleHeader
                        Icon={UserIcon}
                        title1={pageTitle1}
                        title2={pageTitle2}
                        subtext={pageSubtext}
                    />
                </div>



                {isOwnProfile && profileMetrics &&
                    <div className='admin-metric-row mt-4 pb-2'>

                        <div className="container-card dashboard-cards position-relative overflow-hidden" style={{ borderLeft: '4px solid var(--leading-color)' }}>
                            <TenantIcon className='wf-icon text-primary border-0' />
                            <h6 className='fw-normal mt-3 text-secondary'>Account Status</h6>
                            <h5 className='m-0 fw-bold text-capitalize'>{profileMetrics.status}</h5>
                            <div className="decor-circle bg-success"></div>
                        </div>

                        <div className="container-card dashboard-cards position-relative overflow-hidden">
                            <UserIcon className='wf-icon text-primary border-0' />
                            <h6 className='fw-normal mt-3 text-secondary'>Role</h6>
                            <h5 className='m-0 fw-bold text-capitalize'>{profileMetrics.role}</h5>
                            <div className="decor-circle bg-primary"></div>
                        </div>

                        <div className="container-card dashboard-cards position-relative overflow-hidden">
                            <AnalyticsIcon className='wf-icon text-primary border-0' />
                            <h6 className='fw-normal mt-3 text-secondary'>Resource Cap</h6>
                            <h5 className='m-0 fw-bold'>{profileMetrics.resourceCap}</h5>
                            <div className="decor-circle" style={{ background: 'yellow' }}></div>
                        </div>

                        <div className="container-card dashboard-cards position-relative overflow-hidden">
                            <HistoryIcon className='wf-icon text-primary border-0' />
                            <h6 className='fw-normal mt-3 text-secondary'>Quotas Tracked</h6>
                            <h5 className='m-0 fw-bold'>{profileMetrics.quotas}</h5>
                            <div className="decor-circle bg-success"></div>
                        </div>
                    </div>}

                {/* ---------------- RESOURCE SECTION ---------------- */}

                <div className={`d-flex gap-4 admin-content mt-4`}>

                    <div className='admin-content-card1'>


                        <section className='container-card d-flex align-items-end justify-content-between gap-3'>

                            <div className='flex-fill d-flex align-items-start gap-3'>
                                <h2 className={`wf-icon-blue d-flex align-items-center justify-content-center fw-bold m-0 p-2 px-3 plain-wh`}>
                                    {data?.name
                                        ?.split(" ")
                                        .map(word => word[0])
                                        .join("")
                                        .slice(0, 1)
                                        .toUpperCase()}
                                </h2>

                                <div className='flex-fill ms-2'>
                                    <div className='d-a gap-3 flex-wrap'>
                                        <h3 className='fw-bold m-0'>{data?.name}</h3>
                                        <span className={` p-0 text-capitalize border-0 bg-transparent fw-bold ${data?.status === 'active' ? 'wf-icon-green' : 'wf-icon-red'}`} style={{ width: 'fit-content', height: 'fit-content', fontSize: '12px' }}>{data?.status}</span>
                                    </div>
                                    <div className='d-flex flex-wrap  align-items-start align-items-sm-center flex-column flex-sm-row gap-2 mt-1 mb-2'>


                                        <span className=" d-a gap-2 text-capitalize text-secondary">{data?.roles}</span>

                                        <span className='text-secondary d-flex rounded-img d-none d-sm-flex' style={{ width: '5px', height: '5px', background: 'var(--card-color)' }}></span>

                                        <span className='text-secondary text-nowrap'><EmailIcon className="wf-icon-small" />{data?.email}</span>

                                    </div>

                                    <div className='d-a gap-2'>
                                        <small className="text-secondary">
                                            Member Since  {new Date(data?.created_at).toLocaleDateString()}
                                        </small>
                                    </div>

                                </div>
                            </div>

                        </section>

                        <>
                            <h6 className='text-secondary mt-4'>Quick Actions</h6>

                            <div className="row g-3 ">
                                <div className="col-12 col-sm-6 cursor-pointer" onClick={goToSettings}>
                                    <div className="container-card quick-action-cards d-flex align-items-center justify-content-between gap-2 p-3 h-100">
                                        <GearIcon className="wf-icon-green border-0" />
                                        <div className="flex-grow-1">
                                            <h6 className="m-0">
                                                Account Settings
                                            </h6>
                                            <span>
                                            Manage profile, security, and API keys
                                            </span>
                                        </div>
                                        <LinkIcon className="wf-icon-small text-secondary" />
                                    </div>
                                </div>
                            </div>
                        </>

                        {Object.entries(groupedResources)?.length > 0 &&
                            <div className='d-a gap-2 mb-3 mt-4'>
                                <h6 className='text-secondary m-0'>Quota & Resource Usage</h6>
                            </div>}

                        <section className='mt-2 gap-4'>
                            {Object.entries(groupedResources)
                                .sort(([a], [b]) => {
                                    if (a === "core") return -1;
                                    if (b === "core") return 1;
                                    return a.localeCompare(b);
                                })
                                .map(([category, resources]) => (
                                    <section key={category}
                                        className='container-card admin-quota-category mb-4 p-4'>

                                        <h6 className='fw-bold mb-4 text-capitalize'>
                                            {getCategoryIcon(category)}&nbsp;
                                            {category}
                                        </h6>

                                        <div className='admin-quota-grid'>
                                            {resources.map((resource) => {

                                                const usage = Number(resource.usage || 0);
                                                const limit = Number(resource.limit || 0);

                                                const percentage =
                                                    limit > 0
                                                        ? Math.min((usage / limit) * 100, 100)
                                                        : 0;

                                                return (
                                                    <div key={resource.key}
                                                        className='admin-quota-item'>

                                                        <div className='d-flex justify-content-between mb-1 flex-wrap gap-2 admin-quota-item__header'>
                                                            <h6 className='fw-semibold'>
                                                                {resource.key
                                                                    .replace(/_/g, " ")
                                                                    .replace(/\b\w/g, l => l.toUpperCase())}
                                                            </h6>

                                                            <span>
                                                                {limit >= 9999
                                                                    ? "Unlimited"
                                                                    : `${usage.toFixed(2)} / ${limit} ${resource.unit?.toUpperCase() !== "COUNT"
                                                                        ? resource.unit
                                                                        : ""}`}
                                                            </span>
                                                        </div>

                                                        <div
                                                            className="admin-quota-item__bar"
                                                            style={{
                                                                height: "8px",
                                                                background: "var(--card-color)",
                                                                borderRadius: "6px",
                                                                overflow: "hidden",
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    width: `${percentage}%`,
                                                                    height: "100%",
                                                                    background: getColor(percentage),
                                                                    transition: "width 0.3s ease",
                                                                }}
                                                            />
                                                        </div>

                                                        <div className='text-secondary mt-2 small-font admin-quota-item__consumed'>
                                                            CONSUMED: {percentage.toFixed(0)}%
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </section>
                                ))}
                        </section>
                    </div>

                    <div className='admin-content-card2'>
                        <RecentLogs
                            className={'bg-main-container text-white'}
                            viewFrom={'User'}
                            tenantId={data?.tenant_id || data?.tenant?.id}
                            userName={data?.name}
                            isOwnProfile={isOwnProfile}
                        />
                    </div>
                </div>
            </div>

            <div className="mobile-view-bottom"></div>
        </>
    );
};
